import {
  IToolboxComponent,
  ConfigurableFormItem,
  validateConfigurableComponentSettings,
  useAvailableConstantsData,
  IConfigurableFormComponent,
  useForm,
} from "@shesha-io/reactjs";
import React, { useMemo } from "react";
import CallRecordingPlayer from "../../components/callRecordingPlayer";
import { settingsForm } from "./settings";
import { CustomerServiceOutlined } from "@ant-design/icons";
import { evaluateString } from "@shesha-io/reactjs";

// Helper to get error message from unknown error type
const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
};

/**
 * Every setting is optional. IToolboxComponent<TModel> is invariant in TModel - Factory takes
 * it - so a component whose model declares required properties cannot be placed in an
 * IToolboxComponentGroup, whose members are IToolboxComponent<IConfigurableFormComponent>.
 * Optional settings make IConfigurableFormComponent assignable and the group typechecks with
 * no cast. It is also the truthful shape: these values come from designer-authored JSON.
 */
export interface ICallRecordingPlayerProps extends IConfigurableFormComponent {
  // label is inherited: the base declares it as ReactNode, and narrowing it to string here
  // breaks assignability of IConfigurableFormComponent to this interface.
  width?: string;
  height?: string;
  name?: string;
  url?: string;
  recordingId?: string;
  recordingDate?: string;
  hideLabel?: boolean;
  showDownloadButton?: boolean;
  showControls?: boolean;
  compact?: boolean;
  autoplay?: boolean;
  loop?: boolean;
  preload?: 'none' | 'metadata' | 'auto';
  downloadButtonText?: string;
  loadingText?: string;
  noRecordingText?: string;
  // Style properties
  backgroundColor?: string;
  borderColor?: string;
  borderRadius?: number;
  borderWidth?: number;
  padding?: number;
}

const CallRecordingPlayerWrapper: React.FC<ICallRecordingPlayerProps> = (props) => {
  const {
    propertyName,
    label,
    url,
    recordingId,
    recordingDate,
    showDownloadButton,
    showControls,
    compact,
    autoplay,
    loop,
    preload,
    downloadButtonText,
    loadingText,
    noRecordingText,
    backgroundColor,
    borderColor,
    borderRadius,
    borderWidth,
    padding,
    width,
    height
  } = props;
  const allData = useAvailableConstantsData();
  const { formMode, formData } = useForm();

  // Evaluate the ID from form data context if not explicitly provided
  const evaluatedId = useMemo(() => {
    if (recordingId) {
      // If recordingId is provided, evaluate it as it might contain expressions like {{data.id}}
      try {
        return evaluateString(recordingId, allData);
      } catch (error: unknown) {
        const errorMsg = getErrorMessage(error);
        console.error("Error evaluating recording ID:", errorMsg, "Expression:", recordingId);
        return recordingId;
      }
    }
    // Default to data.id if not specified
    // formData and allData.data are both typed `object` in 0.46, so `in` narrows them without
    // an assertion.
    // recordingId is consumed as a string, so ids arriving as numbers are converted and
    // anything else is discarded rather than being passed through as unknown.
    const idOf = (source: object | undefined): string | undefined => {
      const value = source && 'id' in source ? source.id : undefined;
      if (typeof value === 'string') return value;
      if (typeof value === 'number') return String(value);
      return undefined;
    };

    return idOf(formData) ?? idOf(allData?.data);
  }, [recordingId, formData, allData]);

  // Evaluate the recording date expression
  const evaluatedDate = useMemo(() => {
    if (recordingDate) {
      try {
        return evaluateString(recordingDate, allData);
      } catch (error: unknown) {
        const errorMsg = getErrorMessage(error);
        console.error("Error evaluating recording date:", errorMsg, "Expression:", recordingDate);
        return recordingDate;
      }
    }
    return undefined;
  }, [recordingDate, allData]);

  // Determine display label: use label if provided, otherwise propertyName, otherwise default
  // label is ReactNode on the base but the player renders it as a plain string, so a
  // non-string label falls back rather than being coerced into '[object Object]'.
  const displayLabel = (typeof label === 'string' ? label : undefined) || propertyName || 'Call Recording';

  // In designer mode, show a placeholder
  if (formMode === 'designer') {
    return (
      <div style={{
        padding: '16px',
        border: '1px dashed #d9d9d9',
        borderRadius: '8px',
        textAlign: 'center',
        background: '#fafafa',
      }}>
        <CustomerServiceOutlined style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }} />
        <div>Call Recording Player</div>
        <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
          {evaluatedId ? `Recording ID: ${evaluatedId}` : 'No recording ID configured'}
        </div>
      </div>
    );
  }

  return (
    <CallRecordingPlayer
      label={displayLabel}
      url={url}
      recordingId={evaluatedId}
      recordingDate={evaluatedDate}
      showDownloadButton={showDownloadButton}
      showControls={showControls}
      compact={compact}
      autoplay={autoplay}
      loop={loop}
      preload={preload}
      downloadButtonText={downloadButtonText}
      loadingText={loadingText}
      noRecordingText={noRecordingText}
      backgroundColor={backgroundColor}
      borderColor={borderColor}
      borderRadius={borderRadius}
      borderWidth={borderWidth}
      padding={padding}
      width={width}
      height={height}
    />
  );
};

const CallRecordingPlayerToolboxComponent: IToolboxComponent<ICallRecordingPlayerProps> = {
  type: "CallRecordingPlayer",
  name: "Call Recording Player",
  icon: <CustomerServiceOutlined />,
  isInput: false,
  isHidden: false,

  Factory: ({ model }) => {
    return (
      <ConfigurableFormItem model={model}>
        {() => <CallRecordingPlayerWrapper {...model} />}
      </ConfigurableFormItem>
    );
  },

  settingsFormMarkup: settingsForm,
  validateSettings: (model) =>
    validateConfigurableComponentSettings(settingsForm, model),

  initModel: (model) => {
    return {
      ...model,
      name: "callRecordingPlayer",
      propertyName: "callRecording",
      label: "Call Recording",
      url: "/api/services/Telephony/AWS/CallDetails/DownloadRecording?id=",
      recordingId: "{{data.id}}",
      recordingDate: "", // Empty by default - will auto-extract from ID
      hideLabel: false,
      showDownloadButton: true,
      showControls: true,
      compact: false,
      autoplay: false,
      loop: false,
      preload: "metadata",
      downloadButtonText: "Download Recording",
      loadingText: "Loading recording...",
      noRecordingText: "Please provide a recording ID to play or download the recording.",
      // moved from the settings form: 0.46.0 removed defaultValue from the settings types.
      borderWidth: 1,
    };
  },
};

export default CallRecordingPlayerToolboxComponent;
